/**
 * EL IDIOMA DE LA PÁGINA
 * =============================================================================
 * Guarda en qué idioma se está viendo y lo cambia cuando se pulsa ES | EN.
 *
 * -----------------------------------------------------------------------------
 * LO QUE HACE, Y POR QUÉ NO ES SOLO CAMBIAR UNA ETIQUETA
 * -----------------------------------------------------------------------------
 * El selector de idioma cambia TODO el texto de la página: los menús, los títulos,
 * los párrafos, las listas de programas, los avisos del formulario y los mensajes
 * de error. Nada se queda en un idioma fijo.
 *
 * -----------------------------------------------------------------------------
 * SE ACUERDA DE LA ELECCIÓN
 * -----------------------------------------------------------------------------
 * El idioma elegido se guarda en el navegador, así que al volver a la página sigue
 * en el que se eligió. Y la dirección recuerda el idioma (`?lang=en`), de modo que
 * si alguien comparte el enlace, se abre en el mismo idioma.
 *
 * También se cambia el atributo `lang` del documento. Eso importa: es lo que usan
 * los lectores de pantalla para pronunciar bien, y lo que hace que el corrector no
 * marque todo en rojo.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CONFIG } from '../config';
import { TEXTOS, type Idioma, type Textos } from './textos';

interface Contexto {
  idioma: Idioma;
  textos: Textos;
  cambiarIdioma: (nuevo: Idioma) => void;
}

const IdiomaContexto = createContext<Contexto | null>(null);

const CLAVE = 'msoccer-idioma';

/** De dónde sale el idioma inicial, por orden de prioridad. */
function idiomaInicial(): Idioma {
  // 1 · Lo que diga la dirección. Es lo que permite compartir un enlace en inglés.
  if (typeof window !== 'undefined') {
    const enUrl = new URLSearchParams(window.location.search).get('lang');
    if (enUrl === 'en' || enUrl === 'es') return enUrl;
  }

  // 2 · Lo que se eligió la última vez.
  try {
    const guardado = localStorage.getItem(CLAVE);
    if (guardado === 'en' || guardado === 'es') return guardado;
  } catch {
    // Si el navegador no deja guardar —modo privado— no pasa nada: se sigue con
    // el idioma por defecto.
  }

  // 3 · El que esté configurado.
  return CONFIG.idiomaPorDefecto;
}

export function ProveedorIdioma({ children }: { children: ReactNode }) {
  const [idioma, setIdioma] = useState<Idioma>(idiomaInicial);

  const cambiarIdioma = useCallback((nuevo: Idioma) => {
    setIdioma(nuevo);

    try {
      localStorage.setItem(CLAVE, nuevo);
    } catch {
      // Sin permiso para guardar: no es grave.
    }

    // Se actualiza la dirección sin recargar y sin ensuciar el historial: si se
    // usara `pushState` con cada cambio, el botón de atrás tendría que pulsarse
    // cinco veces para salir de la página.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('lang', nuevo);
      window.history.replaceState({}, '', url.toString());
    } catch {
      // Si falla, no pasa nada: el idioma ya cambió en pantalla.
    }
  }, []);

  // El atributo `lang` del documento, que es lo que usan los lectores de pantalla.
  useEffect(() => {
    document.documentElement.lang = idioma;
  }, [idioma]);

  const valor = useMemo<Contexto>(
    () => ({ idioma, textos: TEXTOS[idioma] as Textos, cambiarIdioma }),
    [idioma, cambiarIdioma]
  );

  return <IdiomaContexto.Provider value={valor}>{children}</IdiomaContexto.Provider>;
}

/** Lo usa cualquier componente para leer los textos de su idioma. */
export function useIdioma(): Contexto {
  const ctx = useContext(IdiomaContexto);
  if (!ctx) {
    throw new Error('useIdioma se usó fuera del proveedor de idioma.');
  }
  return ctx;
}
